//alertClauseService.js

import React, { useState } from 'react';
import { message, Modal } from 'antd';
import api from './api';  // Reuse your existing Axios instance
// DAS-2442: direction-aware coloring for the Shareprice (AE_AVG_abs_*) row.
import { sharepriceCellColor, spreadCellColor } from '../helpers/alertColors';

// Export an alert (POST)
export const copyAlert = async (alertData) => {
  try {
    const payload = { ...alertData, copy_request: true }; // Add copy_request flag
    console.log("Exporting alert data with copy request:", payload);
    const response = await api.post("/market_alert", payload);
    message.success("Alert copied successfully!");
    return response.data;
  } catch (error) {
    message.error("Failed to copy alert.");
    console.error("Error copying alert:", error);
    throw error;
  }
};



// Fetch all alerts or one on id (GET)
export const fetchAlerts = async (id = null) => {
  try {
    const response = await api.get("/market_alert", { params: id ? { id } : {} });
    if (id) {
      message.success(`Alert with ID ${id} fetched successfully!`);
      return response.data; // Return a single alert record
    } else {
      message.success("All alerts fetched successfully!");
      return response.data; // Return an array of all alerts
    }
  } catch (error) {
    if (id) {
      message.error(`Error fetching alert with ID ${id}.`);
      console.error(`Error fetching alert with ID ${id}:`, error);
    } else {
      message.error("Error fetching alerts.");
      console.error("Error fetching alerts:", error);
    }
    throw error;
  }
};


// Delete an alert (DELETE)
export const deleteAlert = async (alertId) => {
  try {
    await api.delete("/market_alert", { data: { id: alertId } });
    message.success("Alert deleted successfully!");
  } catch (error) {
    message.error("Failed to delete alert.");
    console.error("Error deleting alert:", error);
    throw error;
  }
};

// Update an alert (PUT)
export const updateAlert = async (updatedAlertData) => {
  const formattedData = { ...updatedAlertData, id: updatedAlertData.ID }; // Use `id` key as expected by backend

  // console.log("AlertData in updateAlert ", formattedData)

  try {
    await api.put("/market_alert", formattedData);
    message.success("Alert updated successfully!");
  } catch (error) {
    message.error("Failed to update alert.");
    console.error("Error updating alert:", error);
    throw error;
  }
};

// Function to fetch all clauses (GET)
export const fetchClauses = async () => {
  try {
    const response = await api.get('/alert_clause_definition');
    message.success('Clauses fetched successfully!');
    return response.data;
  } catch (error) {
    message.error('Error fetching clauses.');
    console.error('Error fetching clauses:', error);
    throw error;
  }
};

// Function to add a new clause (POST)
export const addClause = async (clauseData) => {
  try {
    const response = await api.post('/alert_clause_definition', clauseData);
    message.success('Clause added successfully!');
    return response.data;
  } catch (error) {
    message.error('Error adding clause.');
    console.error('Error adding clause:', error);
    throw error;
  }
};

// Function to update an existing clause (PUT)
export const updateClause = async (id, clauseData) => {
  try {
    const response = await api.put(`/alert_clause_definition/${id}`, clauseData);
    message.success('Clause updated successfully!');
    return response.data;
  } catch (error) {
    message.error('Error updating clause.');
    console.error(`Error updating clause with ID ${id}:`, error);
    throw error;
  }
};

// Function to delete a clause (DELETE)
export const deleteClause = async (id) => {
  try {
    const response = await api.delete(`/alert_clause_definition/${id}`);
    message.success('Clause deleted successfully!');
    return response.data;
  } catch (error) {
    message.error('Error deleting clause.');
    console.error(`Error deleting clause with ID ${id}:`, error);
    throw error;
  }
};

// Function to fetch distinct values for a specific field from a specific table
export const fetchTargetFieldValues = async (table, field) => {
  try {
    const response = await api.get(`/field_values`, {
      params: {
        table: table,
        field: field
      }
    });
    message.success('Field values fetched successfully!');
    // console.log("Field values ", response)
    return response.data;
  } catch (error) {
    message.error('Error fetching target field values.');
    console.error('Error fetching target field values:', error);
    throw error;
  }
};

// Function to test the clause (POST)
export const testClause = async (clauseData) => {
  try {
    const response = await api.post('/test_clause', clauseData);
    // console.log("ClauseData in alertClauseService ", clauseData)
    // console.log("response on clause test in alertClauseservice ", response)
    message.success('Clause tested successfully!');
    return response.data;
  } catch (error) {
    message.error('Error testing clause.');
    console.error('Error testing clause:', error);
    throw error;
  }
};

// Function to test the Alert (POST)
export const testAlert = async (alert_ID) => {
  try {
    // Ensure `alert_ID` is passed in the expected structure
    const response = await api.post('/test_alert', { Alert_ID: alert_ID }, {  // Send as `{ Alert_ID: <value> }`
      headers: {
        'Content-Type': 'application/json' // Ensure content type is set to JSON
      }
    });
    message.success('Alert tested successfully!');
    return response.data;
  } catch (error) {
    message.error('Error testing alert.');
    console.error('Error testing alert:', error);
    throw error;
  }
};


export const createAlert = async (newAlertData) => {
  try {
    console.log("newAlertData in CreateAlert", newAlertData);
    const response = await api.post("/market_alert", newAlertData);
    console.log("response from CreateAlert", response);

    // Merge returned ID with original data
    const savedAlert = { ...newAlertData, ID: response.data.ID };

    message.success("Alert created successfully!");
    return savedAlert; // Return the complete alert object
  } catch (error) {
    message.error("Failed to create alert.");
    console.error("Error creating alert:", error);
    throw error;
  }
};


// Fetch detailed alert records by user and alert parameters (GET)
export const fetchDetailedAlertRecords = async (
  userid,
  alertSubtypeIdentifier,
  alertType,
  alertSubtype,
  targetColumn
) => {
  try {
    const response = await api.get('/get_alerts_from_pf_alert_archive_normalized_for_effectiveness_calculations', {
      params: {
        userid,
        alert_subtype_identifier: alertSubtypeIdentifier,
        alert_type: alertType,
        alert_subtype: alertSubtype,
        target_column: targetColumn, // Pass the target column
      },
    });
    message.success('Detailed alert records fetched successfully!');
    // console.log("Detailed alert records response", response);
    return response.data;
  } catch (error) {
    message.error('Error fetching detailed alert records.');
    console.error('Error fetching detailed alert records:', error);
    throw error;
  }
};

// DAS-2364: Fetch per-part cash + active capital-fit settings for the header
// banner. Soft-fails on error → null so the banner hides gracefully.
//
// Shape: { saxo: {cash_eur, as_of}, degiro_primary: {cash_eur, as_of},
//          target_position_size_eur, preferred_part_for_new_tickers }
export const fetchCapitalFitState = async () => {
  try {
    const response = await api.get("/get_capital_fit_state");
    return response.data || null;
  } catch (error) {
    console.error("Error fetching capital fit state:", error);
    return null;
  }
};

// DAS-2364: localStorage settings — target position size + preferred part.
// Same UX pattern as DAS-2356 advisory thresholds. Frontend-only persistence
// per design Question B (B3). Backend uses env vars; frontend localStorage
// only affects display. If they disagree, the FE can call fetchCapitalFitState
// to see what the backend actually used for the snapshot.
export const DAS2364_LS_KEY_TARGET = "das2364.capital_fit.target_eur";
export const DAS2364_LS_KEY_PART   = "das2364.capital_fit.preferred_part";
export const DAS2364_DEFAULT_TARGET_EUR = 3000;
export const DAS2364_DEFAULT_PART = "degiro";

export const readCapitalFitSettings = () => {
  let target = DAS2364_DEFAULT_TARGET_EUR;
  let part   = DAS2364_DEFAULT_PART;
  try {
    const lsT = window.localStorage.getItem(DAS2364_LS_KEY_TARGET);
    if (lsT !== null && lsT !== "") {
      const parsed = parseFloat(lsT);
      if (Number.isFinite(parsed) && parsed >= 100 && parsed <= 100000) target = parsed;
    }
    const lsP = window.localStorage.getItem(DAS2364_LS_KEY_PART);
    if (lsP === "saxo" || lsP === "degiro") part = lsP;
  } catch (e) { /* localStorage may throw in private mode */ }
  return { targetEur: target, preferredPart: part };
};

export const writeCapitalFitSettings = ({ targetEur, preferredPart }) => {
  try {
    if (targetEur === null) {
      window.localStorage.removeItem(DAS2364_LS_KEY_TARGET);
    } else if (targetEur !== undefined) {
      const v = parseFloat(targetEur);
      if (Number.isFinite(v) && v >= 100 && v <= 100000) {
        window.localStorage.setItem(DAS2364_LS_KEY_TARGET, String(v));
      }
    }
    if (preferredPart === null) {
      window.localStorage.removeItem(DAS2364_LS_KEY_PART);
    } else if (preferredPart === "saxo" || preferredPart === "degiro") {
      window.localStorage.setItem(DAS2364_LS_KEY_PART, preferredPart);
    }
  } catch (e) { /* ignore */ }
};


// DAS-2360: Fetch today's swap-of-the-day recommendation.
//
// Returns the single Swap_Recommendations_Daily row for the current user on
// CURDATE(), or null when the cron hasn't run yet today. The row may be a
// real pair (sell_ticker + buy_ticker populated) OR a no-pair row
// (sell_ticker IS NULL, reason populated) — the UI renders one of two views
// based on that shape.
//
// Soft-fails: on network/server error returns null so the Swaps tab can
// render an "unavailable" state instead of crashing.
export const fetchSwapOfTheDay = async () => {
  try {
    const response = await api.get("/get_swap_of_the_day");
    return response.data || null;
  } catch (error) {
    console.error("Error fetching swap of the day:", error);
    return null;
  }
};

// DAS-2360: Record the user's action on today's swap recommendation.
//
// action ∈ {'taken', 'ignored', 'seen'} — validated server-side too (OWASP A04).
// Used by:
//   - The Swaps tab's auto-mark-seen useEffect (action='seen') — DAS-2350 pattern
//   - The Mark-as-taken / Mark-as-ignored buttons
//
// Returns the API response on success or null on failure (soft-fail).
export const markSwapRecommendation = async (id, action) => {
  try {
    const response = await api.post("/swap_recommendation_action", { id, action });
    return response.data || null;
  } catch (error) {
    console.error(`Error marking swap recommendation ${id} as '${action}':`, error);
    return null;
  }
};

// DAS-3019: the Swap Engine's record — picks, how many could be scored, how
// many beat doing nothing (per window), and how many lack a start price.
export const fetchSwapRecord = async () => {
  try {
    const response = await api.get("/get_swap_record");
    return response.data || null;
  } catch (error) {
    console.error("Error fetching swap record:", error);
    return null;
  }
};

// DAS-2350: Fetch Follow-Through Dashboard data (Activity Tracker + Hindsight).
// Returns { userid, window_days, panel1: [{alert_type, scope, n_fired, n_seen,
// n_followed_up, seen_rate, followup_rate}], panel2: {acted_on, ignored} }.
// On day 1 panel1 will show all-zero rows; panel2 will be empty until ~Nov 2026.
export const fetchAlertFollowThrough = async () => {
  try {
    const response = await api.get("/get_alert_follow_through");
    return response.data || null;
  } catch (error) {
    console.error("Error fetching alert follow-through:", error);
    return null;
  }
};


// DAS-2871 step 3: the "Alerts that are hurting you" tab.
// Returns { horizon_months, min_alerts, proven[], suspected[], worst_but_weak,
// subtypes_total, subtypes_judged, subtypes_not_enough_data }.
// Each verdict row carries n / wins / losses / mean_edge / luck_one_in, so the
// component renders words and never has to do statistics of its own.
// Soft-fails to null: an absent panel beats a broken modal.
export const fetchAlertHurting = async () => {
  try {
    const response = await api.get("/get_alert_hurting");
    return response.data || null;
  } catch (error) {
    console.error("Error fetching alert hurting data:", error);
    return null;
  }
};


// DAS-2871: switch a single alert definition on or off from the verdict tab.
// Purpose-built endpoint — the full market_alert PUT would require sending the
// whole definition back just to flip one flag.
export const setAlertDefinitionStatus = async (id, status) => {
  try {
    const response = await api.post("/set_alert_definition_status", { id, status });
    return response.data || null;
  } catch (error) {
    console.error("Error setting alert definition status:", error);
    throw error;
  }
};


// DAS-2349: Fetch the bucketed confidence × effectiveness correlation.
// Returns { total_alerts_evaluated, userid, final_confidence_buckets, weighted_pct_buckets }
// where each *_buckets entry is { label, n, mean_ae_p_1m, mean_ae_p_6m }.
// On the day this ships, total_alerts_evaluated will be 0 (no archived alerts
// have preserved confidence yet). The UI handles the empty / low-n case.
export const fetchConfidenceCorrelation = async () => {
  try {
    const response = await api.get("/get_confidence_effectiveness_correlation");
    return response.data || null;
  } catch (error) {
    console.error("Error fetching confidence correlation:", error);
    return null;
  }
};


// DAS-2348 / DAS-2356: Fetch the advisory list of under-performing alert subtypes.
//
// Returns rows from Alert_Subtype_Decision_Inputs filtered to the advisory
// criteria. Each row carries the regime-stratified 6m_clean breakdown; the
// consumer picks which regime to spotlight using state.portfolio.data.regime.current.
//
// DAS-2356: thresholds are user-adjustable via the UI. Pass `thresholdPct` /
// `thresholdN` to override the defaults (-5 / 20). Backend validates and
// returns 400 on invalid values; we propagate empty-array on any error so the
// UI fails soft.
export const fetchAlertSubtypeAdvisory = async ({ thresholdPct, thresholdN } = {}) => {
  try {
    const params = {};
    if (thresholdPct !== undefined && thresholdPct !== null && thresholdPct !== "") {
      params.threshold_pct = thresholdPct;
    }
    if (thresholdN !== undefined && thresholdN !== null && thresholdN !== "") {
      params.threshold_n = thresholdN;
    }
    const response = await api.get("/get_alert_subtype_advisory", { params });
    return Array.isArray(response.data) ? response.data : [];
  } catch (error) {
    console.error("Error fetching alert subtype advisory:", error);
    // Soft-fail: an empty advisory is better than a broken modal
    return [];
  }
};

// DAS-2356: localStorage keys + defaults for the advisory thresholds. Shared
// across the AlertEffectiveness modal and the per-alert AlertCard so both
// views read the same active values.
export const DAS2356_LS_KEY_PCT = "das2356.advisory.threshold_pct";
export const DAS2356_LS_KEY_N   = "das2356.advisory.threshold_n";
export const DAS2356_DEFAULT_THRESHOLD_PCT = -5;
export const DAS2356_DEFAULT_THRESHOLD_N   = 20;

/** Read the user's saved advisory thresholds. Returns { thresholdPct, thresholdN }
 *  with defaults when localStorage is empty / corrupted. */
export const readAdvisoryThresholds = () => {
  let pct = DAS2356_DEFAULT_THRESHOLD_PCT;
  let n   = DAS2356_DEFAULT_THRESHOLD_N;
  try {
    const lsPct = window.localStorage.getItem(DAS2356_LS_KEY_PCT);
    if (lsPct !== null && lsPct !== "") {
      const parsed = parseFloat(lsPct);
      if (Number.isFinite(parsed) && parsed > -100 && parsed <= 0) pct = parsed;
    }
    const lsN = window.localStorage.getItem(DAS2356_LS_KEY_N);
    if (lsN !== null && lsN !== "") {
      const parsed = parseInt(lsN, 10);
      if (Number.isFinite(parsed) && parsed >= 1 && parsed <= 1000) n = parsed;
    }
  } catch (e) {
    // localStorage can throw in private mode; ignore and use defaults.
  }
  return { thresholdPct: pct, thresholdN: n };
};

/** Persist the user's advisory thresholds. Pass `null` to clear and revert
 *  to defaults on next read. Range-validated client-side (matches API). */
export const writeAdvisoryThresholds = ({ thresholdPct, thresholdN }) => {
  try {
    if (thresholdPct === null) {
      window.localStorage.removeItem(DAS2356_LS_KEY_PCT);
    } else if (thresholdPct !== undefined) {
      const v = parseFloat(thresholdPct);
      if (Number.isFinite(v) && v > -100 && v <= 0) {
        window.localStorage.setItem(DAS2356_LS_KEY_PCT, String(v));
      }
    }
    if (thresholdN === null) {
      window.localStorage.removeItem(DAS2356_LS_KEY_N);
    } else if (thresholdN !== undefined) {
      const v = parseInt(thresholdN, 10);
      if (Number.isFinite(v) && v >= 1 && v <= 1000) {
        window.localStorage.setItem(DAS2356_LS_KEY_N, String(v));
      }
    }
  } catch (e) {
    // ignore localStorage failures (private mode etc.)
  }
};


// Fetch alert effectiveness data (GET). Returns the bare rows array.
//
// DAS-2347: pass { byRegime: true } to read from Alert_Effectiveness_by_Regime
// (one row per (subtype_identifier, alert_type, alert_subtype, regime)).
// The current market regime is read directly from Redux in the consumer
// (`state.portfolio.data.regime.current`) — not from this endpoint.
export const fetchAlertEffectiveness = async ({ byRegime = false } = {}) => {
  try {
    const url = byRegime ? "/get_alert_effectiveness?by_regime=1" : "/get_alert_effectiveness";
    const response = await api.get(url);
    message.success("Alert effectiveness data fetched successfully!");
    return response.data; // Return the effectiveness data
  } catch (error) {
    message.error("Failed to fetch alert effectiveness data.");
    console.error("Error fetching alert effectiveness data:", error);
    throw error;
  }
};

// Fetch LLM clause recommendations for an alert (POST)
export const fetchLLMRecommendations = async (alertId) => {
  try {
    const response = await api.post('/llm_clause_selector', { alert_id: alertId });
    // Expecting an array of objects: { clause_id, role, rationale, confidence, clause_text, field, operator, threshold, source_table }
    return response.data;
  } catch (error) {
    message.error('Failed to fetch LLM recommendations.');
    console.error('Error fetching LLM recommendations:', error);
    throw error;
  }
};

// Add a clause to affirmative clauses for an alert
export const addAffirmativeClause = async (alertId, clauseId) => {
  try {
    const response = await api.post('/add_affirmative_clause', { 
      alert_id: alertId, 
      clause_id: clauseId 
    });
    message.success(`Clause ${clauseId} added to affirmative clauses successfully!`);
    return response.data;
  } catch (error) {
    message.error('Failed to add clause to affirmative clauses.');
    console.error('Error adding affirmative clause:', error);
    throw error;
  }
};

// Fetch clause tweak suggestions for an alert (POST)
export const fetchClauseTweaks = async (alertId, maxSuggestions = 3) => {
  try {
    const response = await api.post('/clause_tweaks', { 
      alert_id: alertId, 
      max_suggestions: maxSuggestions 
    });
    return response.data;
  } catch (error) {
    message.error('Failed to fetch clause tweak suggestions.');
    console.error('Error fetching clause tweaks:', error);
    throw error;
  }
};

// Convert technical field names to user-friendly labels
const getFieldLabel = (key) => {
  const labelMap = {
    'AE_AVG_abs_1w': 'Avg shareprice move 1 week after alert',
    'AE_AVG_abs_1m': 'Avg shareprice move 1 month after alert',
    'AE_AVG_abs_6m': 'Avg shareprice move 6 months after alert',
    'AE_AVG_abs_1y': 'Avg shareprice move 1 year after alert',
    // DAS-2332: AE_P / AE_DG_P / AE_SP are direction-aware since DAS-2332.
    // For Accumulate: positive = stock beat portfolio. For Harvest: positive = portfolio beat stock (harvesting was value-added).
    'AE_AVG_P_1w': 'Avg edge from following the alert vs holding your full portfolio, 1 week after',
    'AE_AVG_P_1m': 'Avg edge from following the alert vs holding your full portfolio, 1 month after',
    'AE_AVG_P_6m': 'Avg edge from following the alert vs holding your full portfolio, 6 months after',
    'AE_AVG_P_1y': 'Avg edge from following the alert vs holding your full portfolio, 1 year after',
    'AE_DG_P_1w': 'Avg edge from following the alert vs holding your DeGiro portfolio, 1 week after',
    'AE_DG_P_1m': 'Avg edge from following the alert vs holding your DeGiro portfolio, 1 month after',
    'AE_DG_P_6m': 'Avg edge from following the alert vs holding your DeGiro portfolio, 6 months after',
    'AE_DG_P_1y': 'Avg edge from following the alert vs holding your DeGiro portfolio, 1 year after',
    // DAS-2281: SP500 benchmark / DAS-2332: direction-aware
    'AE_AVG_SP_1w': 'Avg edge from following the alert vs holding the S&P 500, 1 week after',
    'AE_AVG_SP_1m': 'Avg edge from following the alert vs holding the S&P 500, 1 month after',
    'AE_AVG_SP_6m': 'Avg edge from following the alert vs holding the S&P 500, 6 months after',
    'AE_AVG_SP_1y': 'Avg edge from following the alert vs holding the S&P 500, 1 year after'
  };
  return labelMap[key] || key;
};

// DAS-2354: Structured (data-only) extraction of effectiveness cells.
//
// Returns: { families: [...], hasAnyData }
//   each family: { id, label, positiveColor, negativeColor, hasData,
//                  cells: { '1w': {raw, clean, n_raw, n_clean}, '1m': {...}, ... } }
//
// The renderer (renderAlertEffectiveness, below) decides whether to display raw
// or clean per cell based on its toggle state. NO JSX is pre-built here — the
// renderer composes the table.
//
// Legacy callers that imported getAlertEffectivenessData for the pre-rendered
// JSX shape are no longer used; everything goes through renderAlertEffectiveness.
export const getAlertEffectivenessData = (effectivenessData) => {
  // Family definitions in display order. positiveColor/negativeColor follow
  // DAS-2332 direction-aware semantics: for the relative-edge families
  // (P / DG_P / SP), negative is bad (red) and positive is good (green).
  // For raw shareprice (abs), the same convention applies for the user's read.
  const FAMILY_DEFS = [
    { id: 'abs', label: 'Shareprice', rawPrefix: 'AE_AVG_abs',  positiveColor: '#3daa6e', negativeColor: '#cf1322' },
    { id: 'P',   label: 'vs Portfolio', rawPrefix: 'AE_AVG_P',  positiveColor: '#3daa6e', negativeColor: '#cf1322' },
    { id: 'DG',  label: 'vs DeGiro',   rawPrefix: 'AE_DG_P',    positiveColor: '#3daa6e', negativeColor: '#cf1322' },
    { id: 'SP',  label: 'vs S&P 500',  rawPrefix: 'AE_AVG_SP',  positiveColor: '#3daa6e', negativeColor: '#cf1322' },
  ];
  const PERIODS = ['1w', '1m', '6m', '1y'];

  const pickNum = (v) => {
    if (v === null || v === undefined || v === '') return null;
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : null;
  };

  const families = FAMILY_DEFS.map((def) => {
    const cells = {};
    let hasData = false;
    PERIODS.forEach((p) => {
      const rawKey = `${def.rawPrefix}_${p}`;
      const cleanKey = `${rawKey}_clean`;
      const nRawKey = `n_${rawKey}`;
      const nCleanKey = `${nRawKey}_clean`;
      const raw    = pickNum(effectivenessData[rawKey]);
      const clean  = pickNum(effectivenessData[cleanKey]);
      const nRaw   = pickNum(effectivenessData[nRawKey]);
      const nClean = pickNum(effectivenessData[nCleanKey]);
      cells[p] = { raw, clean, n_raw: nRaw, n_clean: nClean };
      if (raw !== null || clean !== null) hasData = true;
    });
    return { ...def, cells, hasData };
  });

  return {
    families,
    hasAnyData: families.some((f) => f.hasData),
  };
};

// DAS-2354: per-alert effectiveness in a compact tabular layout.
//
// Replaces the old 4-stacked-text-blocks rendering with:
//   - Optional advisory badge at top (when subtype is on DAS-2348's flagged list)
//   - 4×4 metric grid (Shareprice / vs Portfolio / vs DeGiro / vs S&P 500
//     × 1w / 1m / 6m / 1y) with an n column
//   - Optional regime mini-block (current regime highlighted)
//   - Inline raw/clean toggle (default = clean per DAS-2346)
//
// Optional opts let callers progressively enrich the view:
//   { currentRegime, regimeData, advisoryRows, cleanByDefault }
// Callers that don't pass opts get the table + toggle only (graceful degrade).
//
// Returns a React element (FYI: this is a component-as-function — the toggle
// state needs React hooks. We render a small internal component to host that state.)
const AlertEffectivenessCard = ({ effectivenessData, opts }) => {
  const {
    currentRegime = null,
    regimeData = null,           // array of rows from /api/get_alert_effectiveness?by_regime=1
    advisoryRows = null,         // array of rows from /api/get_alert_subtype_advisory
    cleanByDefault = true,
  } = opts || {};

  const { families, hasAnyData } = getAlertEffectivenessData(effectivenessData);

  // DAS-2354 fix: the DAS-2346 _clean columns are populated by
  // DR_Alert_Effectiveness_update.py's clean aggregation pass. Until that has
  // run for a given user, every *_clean field is NULL while the raw averages
  // ARE populated. If we naively default the toggle to Clean (per DAS-2346's
  // preferred default), the whole table renders as "—" — even though raw data
  // exists. Detect availability per row and fall back to raw when clean is
  // empty; also disable the toggle so the user understands why.
  const hasAnyClean = families.some((f) =>
    Object.values(f.cells).some((c) => c.clean !== null)
  );
  const initialShowClean = cleanByDefault && hasAnyClean;
  const [showClean, setShowClean] = useState(initialShowClean);
  // DAS-2547: does Clean actually change anything for THIS subtype? Compare clean vs raw
  // cells (epsilon guards DECIMAL rounding). When nothing differs, Clean == Raw, so the
  // toggle is a no-op and we say so instead of leaving the user wondering why nothing
  // changes. (n_outliers isn't in this card's /api/portfolio_view payload, so we derive
  // it from the raw/clean cells the card already has.)
  const cleanDiffers = hasAnyClean && families.some((f) =>
    Object.values(f.cells).some((c) =>
      c.clean !== null && c.raw !== null && Math.abs(c.clean - c.raw) >= 0.05
    )
  );
  const cleanIsNoOp = hasAnyClean && !cleanDiffers;
  // DAS-2354 follow-up: click-to-expand a larger version of the same view.
  // The compact form lives in narrow table cells (AlertTable), where 10px text
  // is too small to read comfortably. The modal renders the SAME content with
  // larger fonts and more padding, sharing the same toggle/state.
  const [expanded, setExpanded] = useState(false);

  if (!hasAnyData) {
    return <span style={{ fontSize: '10px', fontStyle: 'italic', color: '#888' }}>No effectiveness data yet available</span>;
  }

  const PERIODS = ['1w', '1m', '6m', '1y'];

  // D. Advisory badge: surface DAS-2348's verdict for this subtype, if flagged.
  // Match by alert_subtype_identifier; supports CSV identifiers for combination alerts.
  const advisoryHit = (advisoryRows || []).find((r) => {
    if (!r?.alert_subtype_identifier) return false;
    const ids = String(r.alert_subtype_identifier).split(',').map((s) => s.trim());
    const target = String(effectivenessData.alert_subtype_identifier || '').trim();
    return ids.includes(target);
  });

  // B. Regime mini-block: find regime-stratified rows for THIS subtype identifier.
  // regimeData is a flat list with a `regime` column; subset to this subtype.
  const regimeRowsForSubtype = (regimeData || []).filter((r) => {
    if (!r?.alert_subtype_identifier) return false;
    return String(r.alert_subtype_identifier).trim() === String(effectivenessData.alert_subtype_identifier || '').trim();
  });
  const regimeByLabel = {};
  regimeRowsForSubtype.forEach((r) => {
    if (r.regime) regimeByLabel[String(r.regime).toUpperCase()] = r;
  });
  const showRegimeBlock = Object.keys(regimeByLabel).length > 0;

  // DAS-2442: direction-aware coloring. The Shareprice row (family.id === 'abs')
  // now flips colors for Harvest/Sell alerts so a falling stock is shown green
  // (the alert was right). The three vs-X rows are already sign-flipped by the
  // backend (DAS-2332) so they use the raw sign rule unchanged.
  const alertTypeForColor = effectivenessData?.alert_type;
  const cellColor = (value, family) => {
    if (value === null || value === undefined) return 'inherit';
    if (family.id === 'abs') {
      return sharepriceCellColor(value, alertTypeForColor);
    }
    return spreadCellColor(value);
  };

  // Render the body at the requested sizing. `size`: 'compact' for inline
  // table cells, 'large' for the click-to-expand modal. The same data, the
  // same toggle state — only typography/padding scale.
  const renderBody = (size) => {
    const isLarge = size === 'large';
    const S = {
      base:        isLarge ? '13px' : '10px',
      cell:        isLarge ? '14px' : '10px',
      cellPad:     isLarge ? '6px 12px' : '2px 4px',
      blockPad:    isLarge ? '10px 14px' : '4px 6px',
      blockMargin: isLarge ? 12 : 6,
      lineHeight:  isLarge ? '1.5' : '1.3',
      regimeLine:  isLarge ? '13px' : '10px',
    };
    const fmtPct = (v) => (v === null || v === undefined)
      ? <span style={{ color: '#bbb' }}>—</span>
      : `${v.toFixed(1)}%`;

    // DAS-2706: the Shareprice (abs) family is the basis for the per-period n
    // shown in the column headers (see thead below).
    const headerNAbsFamily = families.find((f) => f.id === 'abs');

    return (
      <div style={{ fontSize: S.base, lineHeight: S.lineHeight }}>
        {/* D. Advisory badge */}
        {advisoryHit && (
          <div style={{
            padding: S.blockPad,
            marginBottom: S.blockMargin,
            background: '#fff2f0',
            border: '1px solid #ffccc7',
            borderRadius: 3,
            color: '#cf1322',
            fontSize: S.base,
          }}>
            ⚠ <strong>Underperforming subtype</strong> — AE_P_6m_clean ={' '}
            {fmtPct(parseFloat(advisoryHit.ae_p_6m_clean))} over n={advisoryHit.n_clean ?? '?'}.
            {advisoryHit.suggested_action ? ` Suggested: ${advisoryHit.suggested_action}.` : ''}
          </div>
        )}

        {/* Header: title + raw/clean toggle + info tooltip. The expand icon
            lives only in compact mode (no point expanding from inside the modal). */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: isLarge ? 8 : 4 }}>
          <span
            style={{ fontWeight: 'bold', fontSize: S.base }}
            title={
              'Average alert effectiveness across all historical alerts of this subtype.\n' +
              'Shareprice = absolute price move after alert. Colour is direction-aware (DAS-2442): for a Harvest/Sell alert, a falling stock is shown green (alert was right). For Accumulate/Buy, a rising stock is green.\n' +
              'vs Portfolio = edge vs holding your full portfolio (direction-aware since DAS-2332).\n' +
              'vs DeGiro = same but vs DeGiro-only portfolio.\n' +
              'vs S&P 500 = alpha vs the benchmark.\n' +
              'Across all four rows: green = the alert was right, red = the alert was wrong, regardless of alert type.\n' +
              'n = sample size feeding the average. Toggle Clean to exclude DAS-2346 IQR outliers.'
            }
          >
            Alert effectiveness ⓘ
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <label
              style={{
                fontSize: S.base,
                cursor: (hasAnyClean && !cleanIsNoOp) ? 'pointer' : 'not-allowed',
                userSelect: 'none',
                opacity: (hasAnyClean && !cleanIsNoOp) ? 1 : 0.5,
              }}
              title={
                !hasAnyClean
                  ? 'Clean variants (DAS-2346) not yet computed for this subtype — showing raw averages. Will populate after the next DR_Alert_Effectiveness_update run.'
                  : cleanIsNoOp
                    ? 'Clean and Raw are identical for this subtype — it has no IQR outliers to exclude — so the toggle is disabled here. It activates only when excluding outliers actually changes the averages.'
                    : 'Excludes IQR-flagged outliers (DAS-2346) from the averages — for this subtype it changes the numbers.'
              }
            >
              <input
                type="checkbox"
                checked={showClean && !cleanIsNoOp}
                disabled={!hasAnyClean || cleanIsNoOp}
                onChange={(e) => setShowClean(e.target.checked)}
                style={{ verticalAlign: 'middle', marginRight: 4 }}
              />
              Clean
              <span style={{ color: cleanIsNoOp ? '#bbb' : cleanDiffers ? '#faad14' : '#888', marginLeft: 4 }}>
                {cleanIsNoOp ? '(= Raw)' : cleanDiffers ? '(changes ⚠)' : 'ⓘ'}
              </span>
            </label>
            {!isLarge && (
              <button
                type="button"
                onClick={() => setExpanded(true)}
                title="Open larger view"
                style={{
                  background: 'transparent',
                  border: '1px solid #d9d9d9',
                  borderRadius: 3,
                  cursor: 'pointer',
                  color: '#555',
                  fontSize: '11px',
                  padding: '0 5px',
                  lineHeight: '16px',
                }}
              >
                ⛶
              </button>
            )}
          </span>
        </div>

        {/* A. 4×4 grid (compact or large depending on size) */}
        <table style={{ width: '100%', fontSize: S.cell, borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid #ddd' }}>
              <th style={{ textAlign: 'left', padding: S.cellPad, color: '#666', fontWeight: 'normal' }}></th>
              {PERIODS.map((p) => {
                // DAS-2706: show each period's sample size (n) in the header itself,
                // not only on cell hover. Basis = the Shareprice (abs) family — it's the
                // count of alerts old enough to have data at this horizon (the relative
                // metrics can only be ≤ this). Respects the Clean toggle; falls back to
                // the largest n across families when abs is missing.
                const absCell = (headerNAbsFamily && headerNAbsFamily.cells[p]) || {};
                let headerN = showClean ? absCell.n_clean : absCell.n_raw;
                if (headerN === null || headerN === undefined) {
                  const alt = families
                    .map((f) => { const c = f.cells[p] || {}; return showClean ? c.n_clean : c.n_raw; })
                    .filter((n) => n !== null && n !== undefined);
                  headerN = alt.length ? Math.max(...alt) : null;
                }
                return (
                  <th key={p} style={{ textAlign: 'right', padding: S.cellPad, color: '#666', fontWeight: 'normal' }}>
                    <div>{p}</div>
                    <div style={{ fontSize: isLarge ? '11px' : '9px', color: '#999', fontWeight: 'normal' }}>
                      {headerN !== null && headerN !== undefined ? `(n=${headerN})` : '(n=—)'}
                    </div>
                  </th>
                );
              })}
              <th style={{ textAlign: 'right', padding: S.cellPad, color: '#666', fontWeight: 'normal' }} title="Sample size at the 6-month horizon (the canonical advisory window). Each period has its own n — hover any value to see it; shorter windows usually have a larger n because more alerts are old enough to measure.">n (6m)</th>
            </tr>
          </thead>
          <tbody>
            {families.filter((f) => f.hasData).map((family) => {
              const sixMonthCell = family.cells['6m'] || {};
              const nFor6m = showClean ? sixMonthCell.n_clean : sixMonthCell.n_raw;
              return (
                <tr key={family.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                  <td style={{ padding: S.cellPad, color: '#444' }}>{family.label}</td>
                  {PERIODS.map((p) => {
                    const cell = family.cells[p] || {};
                    const value = showClean ? cell.clean : cell.raw;
                    // DAS-2547/2548: per-cell sample size on hover (each period has its own n).
                    const nCell = showClean ? cell.n_clean : cell.n_raw;
                    return (
                      <td key={p}
                        title={`${family.label} ${p}: n = ${nCell !== null && nCell !== undefined ? nCell : '—'}`}
                        style={{
                        textAlign: 'right',
                        padding: S.cellPad,
                        color: cellColor(value, family),
                        fontWeight: value !== null && value !== undefined ? 'bold' : 'normal',
                      }}>
                        {fmtPct(value)}
                      </td>
                    );
                  })}
                  <td style={{ textAlign: 'right', padding: S.cellPad, color: '#888' }}>
                    {nFor6m !== null && nFor6m !== undefined ? nFor6m : (<span style={{ color: '#bbb' }}>—</span>)}
                    {nFor6m !== null && nFor6m !== undefined && nFor6m < 6 && (
                      <span style={{ color: '#faad14', marginLeft: 2 }} title="Small sample">⚠</span>
                    )}
                  </td>
                </tr>
              );
            })}
            {/* DAS-2548: S&P 500 hit ratio (win-rate) for all timeframes — % of this
                subtype's alerts that beat just holding SPY. No raw/clean variant, so it
                is the same under the Clean toggle. */}
            {(() => {
              const hr = (p) => {
                const v = effectivenessData[`hit_rate_sp_${p}`];
                return (v === null || v === undefined || v === '') ? null : parseFloat(v);
              };
              if (!PERIODS.some((p) => hr(p) !== null)) return null;
              const nSp = effectivenessData.n_AE_AVG_SP_6m;
              return (
                <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                  <td
                    style={{ padding: S.cellPad, color: '#444' }}
                    title="% of this subtype's alerts that beat just holding the S&P 500 (SPY) over each window. ≥50% = the alert usually beats the index. Not affected by the Clean toggle."
                  >
                    S&amp;P 500 win&nbsp;%
                  </td>
                  {PERIODS.map((p) => {
                    const v = hr(p);
                    const nSpP = effectivenessData[`n_AE_AVG_SP_${p}`];  // DAS-2548: per-cell n on hover
                    const color = v === null ? '#bbb' : v >= 50 ? '#3daa6e' : '#cf1322';
                    return (
                      <td key={p}
                        title={`S&P 500 win % ${p}: n = ${nSpP !== null && nSpP !== undefined ? nSpP : '—'}`}
                        style={{
                        textAlign: 'right',
                        padding: S.cellPad,
                        color,
                        fontWeight: v !== null ? 'bold' : 'normal',
                      }}>
                        {v === null ? <span style={{ color: '#bbb' }}>—</span> : `${v.toFixed(1)}%`}
                      </td>
                    );
                  })}
                  <td style={{ textAlign: 'right', padding: S.cellPad, color: '#888' }}>
                    {nSp !== null && nSp !== undefined ? nSp : (<span style={{ color: '#bbb' }}>—</span>)}
                  </td>
                </tr>
              );
            })()}
          </tbody>
        </table>

        {/* B. Regime mini-block */}
        {showRegimeBlock && (
          <div style={{ marginTop: S.blockMargin, padding: S.blockPad, background: '#f6ffed', border: '1px solid #d9f7be', borderRadius: 3 }}>
            <div style={{ fontSize: S.regimeLine, color: '#444', marginBottom: 4 }}>
              <strong>Regime split</strong> (AE_P_6m{showClean ? '_clean' : ''})
              {currentRegime ? <span style={{ color: '#666' }}> — current: <strong>{currentRegime}</strong></span> : null}
            </div>
            {['BULL', 'NEUTRAL', 'BEAR'].map((label) => {
              const row = regimeByLabel[label];
              if (!row) return null;
              const valueKey = showClean ? 'AE_AVG_P_6m_clean' : 'AE_AVG_P_6m';
              const nKey     = showClean ? 'n_AE_AVG_P_6m_clean' : 'n_AE_AVG_P_6m';
              const rawValue = parseFloat(row[valueKey]);
              const value = Number.isFinite(rawValue) ? rawValue : null;
              const n = row[nKey];
              const isCurrent = currentRegime && String(currentRegime).toUpperCase() === label;
              const valueColor = value === null ? '#bbb' : value > 0 ? '#3daa6e' : value < 0 ? '#cf1322' : 'inherit';
              const lowN = (typeof n === 'number' && n < 10);
              return (
                <div key={label} style={{ fontSize: S.regimeLine, display: 'flex', justifyContent: 'space-between', opacity: lowN ? 0.6 : 1 }}>
                  <span>
                    {isCurrent ? '▸ ' : '  '}{label}
                    {lowN && <span style={{ color: '#faad14', marginLeft: 4 }} title="n<10: treat as indicative">?</span>}
                  </span>
                  <span>
                    <span style={{ color: valueColor, fontWeight: isCurrent ? 'bold' : 'normal' }}>{fmtPct(value)}</span>
                    <span style={{ color: '#888', marginLeft: 6 }}>(n={n ?? '—'})</span>
                  </span>
                </div>
              );
            })}
          </div>
        )}

        {/* In the large view: empty-state hint when regime data is missing,
            so users on DEV (pre-backfill) understand why the block isn't shown. */}
        {isLarge && !showRegimeBlock && (
          <div style={{
            marginTop: S.blockMargin,
            padding: S.blockPad,
            background: '#fafafa',
            border: '1px dashed #d9d9d9',
            borderRadius: 3,
            color: '#888',
            fontSize: S.regimeLine,
            fontStyle: 'italic',
          }}>
            No regime-stratified data yet for this subtype — runs after DR_Alert_Effectiveness_regime_backfill
            populates Alert_Effectiveness_by_Regime (DAS-2347).
          </div>
        )}
      </div>
    );
  };

  const modalTitle = (
    <span>
      Alert Effectiveness
      {effectivenessData.alert_subtype ? ` — ${effectivenessData.alert_subtype}` : ''}
      {effectivenessData.alert_subtype_identifier ? (
        <span style={{ color: '#888', marginLeft: 8, fontSize: '12px' }}>
          (id {effectivenessData.alert_subtype_identifier})
        </span>
      ) : null}
    </span>
  );

  return (
    <>
      {renderBody('compact')}
      <Modal
        open={expanded}
        onCancel={() => setExpanded(false)}
        footer={null}
        width={820}
        title={modalTitle}
        destroyOnClose
        // DAS-2547: the parent "Alert Details" is an MUI Dialog at z-index 1300; the
        // antd Modal defaults to 1000 and rendered BEHIND it. Lift above the whole
        // modal stack (Dialog 1300 / Jira dialog 1500 / MarketAlerts 2000).
        zIndex={3000}
      >
        {renderBody('large')}
      </Modal>
    </>
  );
};

export const renderAlertEffectiveness = (alertEffectiveness, alertID, opts) => {
  if (!Array.isArray(alertEffectiveness) || alertEffectiveness.length === 0) {
    return <span style={{ fontSize: '10px', color: '#888' }}>No data available</span>;
  }

  const effectivenessData = alertEffectiveness.find(
    (eff) => eff.alert_subtype_identifier === alertID
  );

  if (!effectivenessData) {
    return <span style={{ fontSize: '10px', color: '#888' }}>No data available</span>;
  }

  return <AlertEffectivenessCard effectivenessData={effectivenessData} opts={opts} />;
};

export const fetchClauseProposals = async (alertId, maxSuggestions = 3) => {
  try {
    const response = await api.post('/llm_clause_proposals', { 
      alert_id: alertId, 
      max_suggestions: maxSuggestions 
    });
    return response.data;
  } catch (error) {
    message.error('Failed to fetch clause proposals.');
    console.error('Error fetching clause proposals:', error);
    throw error;
  }
};

// DAS-2273: Promote a DEV alert definition (and its referenced clauses)
// to PROD. The backend defaults to dry_run=true so this is a safe preview
// call. Pass commit=true to actually write.
export const promoteAlertToProd = async (
  devAlertId,
  { commit = false, overwriteExisting = false } = {}
) => {
  try {
    const response = await api.post('/market_alert/promote_to_prod', {
      dev_alert_id: devAlertId,
      overwrite_existing: overwriteExisting,
      dry_run: !commit,
    });
    return response.data;
  } catch (error) {
    // 409 collision is a normal result, not a failure — surface payload.
    if (error?.response?.status === 409) {
      return error.response.data;
    }
    console.error('Error promoting alert to PROD:', error);
    throw error;
  }
};


// DAS-2277: Promote a single clause from DEV to PROD.
// Defaults to dry_run=true (preview). Pass commit=true to write.
export const promoteClauseToProd = async (
  devClauseId,
  { commit = false } = {}
) => {
  try {
    const response = await api.post('/clause/promote_to_prod', {
      dev_clause_id: devClauseId,
      dry_run: !commit,
    });
    return response.data;
  } catch (error) {
    console.error('Error promoting clause to PROD:', error);
    throw error;
  }
};


export const addKnockoutClause = async (alertId, clauseId) => {
  try {
    const response = await api.post('/add_knockout_clause', { 
      alert_id: alertId, 
      clause_id: clauseId 
    });
    message.success(`Clause ${clauseId} added to knockout clauses successfully!`);
    return response.data;
  } catch (error) {
    message.error('Failed to add clause to knockout clauses.');
    console.error('Error adding knockout clause:', error);
    throw error;
  }
};

